import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { authApi } from '../../api/auth';
import { usersApi } from '../../api/users';
import { useLanguage } from '../../i18n/LanguageContext';
import '../../styles/football-theme.css';

const LanguageSelection = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState(language || 'en');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const currentUser = authApi.getCurrentUser();
    setUser(currentUser);
    if (currentUser?.language) {
      setSelectedLanguage(currentUser.language);
    }
  }, []);

  useEffect(() => {
    if (language) {
      setSelectedLanguage(language);
    }
  }, [language]);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
  ];

  const handleContinue = async () => {
    setLoading(true);
    try {
      // Save language preference to backend
      if (user?.id) {
        await usersApi.updateLanguage(user.id, selectedLanguage);
        // Update local storage
        const updatedUser = { ...user, language: selectedLanguage };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      setLanguage(selectedLanguage);
      
      // Navigate to tutorial after language selection
      navigate('/onboarding/tutorial');
    } catch (error) {
      console.error('Error updating language:', error);
      // Still navigate even if update fails
      setLanguage(selectedLanguage);
      navigate('/onboarding/tutorial');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen football-background stadium-lights flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="football-card glass-football p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full mb-4 trophy-glow">
              <Globe className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">{t('onboarding.chooseLanguageTitle')}</h2>
            <p className="text-gray-300">{t('onboarding.chooseLanguageSubtitle')}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => setSelectedLanguage(lang.code)}
                className={`p-6 rounded-lg border-2 transition-all ${
                  selectedLanguage === lang.code
                    ? 'border-yellow-400 bg-yellow-400/20 scale-105'
                    : 'border-gray-600 bg-gray-800/50 hover:border-gray-500'
                }`}
              >
                <div className="text-4xl mb-2">{lang.flag}</div>
                <div className={`font-semibold ${
                  selectedLanguage === lang.code ? 'text-yellow-400' : 'text-gray-300'
                }`}>
                  {lang.name}
                </div>
              </button>
            ))}
          </div>

          <button
            onClick={handleContinue}
            disabled={loading}
            className="btn-football w-full text-lg py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('onboarding.savingLanguage') : t('common.continue')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelection;

